/**
 * Hero Search auto-tag pills for compound person queries.
 * Sync classification (no network) — used on blur / Enter / GO.
 *
 * Pill types (unified): name | location | focus (context) | username.
 * Employer and profession both map to ``focus`` → SSE ``context_focus``.
 */

import { splitPersonInlineFocus } from './investigationWorkspaceUtils';

const RO_LOCATION_WORDS = new Set([
  'iasi', 'iași', 'bucuresti', 'bucurești', 'bucharest', 'cluj', 'cluj-napoca',
  'timisoara', 'timișoara', 'brasov', 'brașov', 'constanta', 'constanța',
  'craiova', 'galati', 'galați', 'oradea', 'sibiu', 'ploiesti', 'ploiești',
  'bacau', 'bacău', 'arad', 'pitesti', 'pitești', 'suceava', 'romania', 'românia',
]);

/** Mirrors backend ``_COMMON_GIVEN_NAME_FRAGMENTS`` for compound RO/EN identity. */
const COMMON_GIVEN_NAMES = new Set([
  'marius', 'dan', 'daniel', 'ion', 'ioan', 'andrei', 'alex', 'alexandru',
  'maria', 'ana', 'ioana', 'elena', 'mihai', 'cristian', 'adrian', 'george',
  'vlad', 'radu', 'stefan', 'florin', 'gabriel', 'nicolae', 'constantin',
  'vasile', 'lucian', 'bogdan', 'catalin', 'cătalin', 'ciprian', 'sorin',
  'alina', 'andreea', 'cristina', 'diana', 'irina', 'simona', 'raluca',
  'john', 'jane', 'michael', 'david', 'james', 'robert', 'mary', 'anna',
  'paul', 'peter', 'thomas', 'mark', 'luke', 'sarah', 'emma',
]);

const GEO_SINGLE = {
  romania: 'Romania',
  românia: 'Romania',
  uk: 'United Kingdom',
  england: 'England',
  germany: 'Germany',
  france: 'France',
  italy: 'Italy',
  spain: 'Spain',
  netherlands: 'Netherlands',
  belgium: 'Belgium',
  austria: 'Austria',
  poland: 'Poland',
  hungary: 'Hungary',
  bulgaria: 'Bulgaria',
  usa: 'United States',
};

/** @typedef {'name' | 'location' | 'focus' | 'username'} PersonPillType */

/** @type {PersonPillType[]} */
export const PILL_TYPE_CYCLE = ['name', 'location', 'focus', 'username'];

export const PILL_TYPE_META = {
  name: { emoji: '👤', label: 'Name', title: 'Person name' },
  location: { emoji: '📍', label: 'Location', title: 'Location' },
  focus: {
    emoji: '🏢',
    label: 'Context',
    title: 'Context (employer, role, or organization)',
  },
  username: { emoji: '@', label: 'Username', title: 'Username handle' },
};

let pillSeq = 0;

function nextPillId() {
  pillSeq += 1;
  return `pp-${pillSeq}-${Date.now().toString(36)}`;
}

function normalizeWhitespace(value) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim();
}

function titleCasePhrase(phrase) {
  return normalizeWhitespace(phrase)
    .split(' ')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .filter(Boolean)
    .join(' ');
}

/**
 * Normalize legacy pill types (``company`` → ``focus``).
 *
 * @param {string} type
 * @returns {PersonPillType}
 */
export function normalizePersonPillType(type) {
  const t = String(type || '').trim().toLowerCase();
  if (t === 'company' || t === 'employer' || t === 'context') return 'focus';
  if (t === 'name' || t === 'location' || t === 'focus' || t === 'username') {
    return /** @type {PersonPillType} */ (t);
  }
  return 'focus';
}

function looksLikeUsername(token) {
  const t = String(token || '').trim();
  if (!t || /\s/.test(t)) return false;
  if (t.startsWith('@')) return /^@[a-zA-Z0-9_.-]{2,30}$/.test(t);
  if (!/[0-9_]/.test(t)) return false;
  return /^[a-zA-Z0-9_.-]{3,30}$/.test(t);
}

function isLocationToken(token) {
  const lower = String(token || '')
    .replace(/^[.,;]+|[.,;]+$/g, '')
    .toLowerCase();
  if (!lower) return false;
  if (RO_LOCATION_WORDS.has(lower) || GEO_SINGLE[lower]) return true;
  return false;
}

function isCommonGivenName(token) {
  const lower = String(token || '')
    .replace(/^[.,;:!?()[\]"']+/g, '')
    .replace(/[.,;:!?()[\]"']+$/g, '')
    .toLowerCase();
  return Boolean(lower) && COMMON_GIVEN_NAMES.has(lower);
}

/**
 * Absorb contiguous given-name crumbs into the name token list
 * (e.g. Sarb Mihai + Vlad → Sarb Mihai Vlad).
 *
 * @param {string[]} nameTokens
 * @param {string[]} residual
 * @returns {{ nameTokens: string[], residual: string[] }}
 */
function absorbGivenNameResiduals(nameTokens, residual) {
  const name = [...(nameTokens || [])];
  const left = [];
  let absorbing = true;
  for (const tok of residual || []) {
    const core = String(tok || '').replace(/^[.,;]+|[.,;]+$/g, '');
    if (!core) continue;
    const followsMihai = name.length > 0 && name[name.length - 1].toLowerCase() === 'mihai';
    if (absorbing && isCommonGivenName(core)) {
      name.push(core);
      continue;
    }
    absorbing = false;
    left.push(core);
  }
  return { nameTokens: name, residual: left };
}

function classifyResidualToken(token) {
  const raw = String(token || '').trim();
  if (!raw) return null;
  if (isCommonGivenName(raw)) {
    // Should already be absorbed into identity; never emit as company/context.
    return null;
  }
  if (looksLikeUsername(raw)) {
    return { type: 'username', text: raw.replace(/^@/, '') };
  }
  if (isLocationToken(raw)) {
    return { type: 'location', text: titleCasePhrase(raw) };
  }
  // Employer, profession, or unknown residual → unified Context (focus).
  return { type: 'focus', text: titleCasePhrase(raw) };
}

/**
 * Whether the free-text query is worth materializing into pills.
 *
 * @param {string} text
 * @returns {boolean}
 */
export function shouldMaterializePersonPills(text) {
  const raw = normalizeWhitespace(text);
  if (!raw) return false;
  const { input: identityPart } = splitPersonInlineFocus(raw);
  const identity = normalizeWhitespace(identityPart) || raw;
  const tokens = identity.split(/\s+/).filter(Boolean);
  if (tokens.length >= 3) return true;
  // Name + location (e.g. "Ion Popescu Iasi") → separate location chip.
  if (tokens.length >= 2 && tokens.some((t) => isLocationToken(t))) return true;
  return false;
}

/**
 * Build interactive pills from a compound person query (client-side).
 *
 * @param {string} text
 * @returns {{ id: string, type: PersonPillType, text: string }[]}
 */
export function buildPersonPillsFromQuery(text) {
  const raw = normalizeWhitespace(text);
  if (!raw) return [];

  const { input: identityPart, contextTags: explicitFocus } = splitPersonInlineFocus(raw);
  const identity = normalizeWhitespace(identityPart) || raw;
  const tokens = identity.split(/\s+/).filter(Boolean);

  if (tokens.length === 0) return [];

  /** @type {{ id: string, type: PersonPillType, text: string }[]} */
  const pills = [];

  if (tokens.length === 1) {
    pills.push({
      id: nextPillId(),
      type: 'name',
      text: titleCasePhrase(tokens[0]),
    });
  } else if (tokens.length === 2 && isLocationToken(tokens[1])) {
    pills.push({
      id: nextPillId(),
      type: 'name',
      text: titleCasePhrase(tokens[0]),
    });
    pills.push({
      id: nextPillId(),
      type: 'location',
      text: titleCasePhrase(tokens[1]),
    });
  } else if (tokens.length === 2) {
    pills.push({
      id: nextPillId(),
      type: 'name',
      text: titleCasePhrase(tokens.join(' ')),
    });
  } else {
    let nameTokens = tokens.slice(0, 2);
    let residual = tokens.slice(2);
    // Pull locations out of residual first so given-name absorb sees a clean tail.
    const earlyLocs = [];
    const nonLocResidual = [];
    for (const tok of residual) {
      if (isLocationToken(tok)) earlyLocs.push(tok);
      else nonLocResidual.push(tok);
    }
    ({ nameTokens, residual } = absorbGivenNameResiduals(nameTokens, nonLocResidual));
    residual = [...earlyLocs, ...residual];
    pills.push({
      id: nextPillId(),
      type: 'name',
      text: titleCasePhrase(nameTokens.join(' ')),
    });
    for (const tok of residual) {
      const classified = classifyResidualToken(tok);
      if (!classified) continue;
      pills.push({
        id: nextPillId(),
        type: /** @type {PersonPillType} */ (classified.type),
        text: classified.text,
      });
    }
  }

  for (const focus of explicitFocus || []) {
    const t = normalizeWhitespace(focus);
    if (!t) continue;
    pills.push({
      id: nextPillId(),
      type: 'focus',
      text: titleCasePhrase(t),
    });
  }

  return pills;
}

/**
 * Cycle pill type on click (name → location → focus → username → name).
 *
 * @param {string} type
 * @returns {PersonPillType}
 */
export function cyclePersonPillType(type) {
  const normalized = normalizePersonPillType(type);
  const idx = PILL_TYPE_CYCLE.indexOf(normalized);
  if (idx < 0) return 'focus';
  return PILL_TYPE_CYCLE[(idx + 1) % PILL_TYPE_CYCLE.length];
}

/**
 * @param {{ id: string, type: string, text: string }[]} pills
 * @param {string} pillId
 * @returns {{ id: string, type: PersonPillType, text: string }[]}
 */
export function cyclePillById(pills, pillId) {
  return (pills || []).map((p) => {
    const baseType = normalizePersonPillType(p.type);
    if (p.id !== pillId) {
      return { ...p, type: baseType };
    }
    const nextType = cyclePersonPillType(baseType);
    let text = p.text;
    if (nextType === 'username') {
      text = String(text || '').replace(/^@/, '');
    }
    return { ...p, type: nextType, text };
  });
}

/**
 * Set a pill to an explicit type (hero type selector).
 *
 * @param {{ id: string, type: string, text: string }[]} pills
 * @param {string} pillId
 * @param {string} nextType
 * @returns {{ id: string, type: PersonPillType, text: string }[]}
 */
export function setPillTypeById(pills, pillId, nextType) {
  const target = normalizePersonPillType(nextType);
  return (pills || []).map((p) => {
    if (p.id !== pillId) {
      return { ...p, type: normalizePersonPillType(p.type) };
    }
    let text = p.text;
    if (target === 'username') {
      text = String(text || '').replace(/^@/, '');
    }
    return { ...p, type: target, text };
  });
}

/**
 * Rebuild a free-text query from pills (for detection / submit fallback).
 *
 * @param {{ type: string, text: string }[]} pills
 * @returns {string}
 */
export function composeQueryFromPills(pills) {
  const list = Array.isArray(pills) ? pills : [];
  const name = list
    .filter((p) => normalizePersonPillType(p.type) === 'name')
    .map((p) => p.text)
    .join(' ');
  const rest = list
    .filter((p) => normalizePersonPillType(p.type) !== 'name')
    .map((p) => {
      const t = normalizePersonPillType(p.type);
      return t === 'username' ? `@${String(p.text || '').replace(/^@/, '')}` : p.text;
    });
  return normalizeWhitespace([name, ...rest].filter(Boolean).join(' '));
}

/**
 * Map pills into person stream / tab-storage tag shape.
 *
 * @param {{ type: string, text: string }[]} pills
 * @returns {{
 *   name: string,
 *   locationTags: string[],
 *   contextTags: string[],
 *   usernameTags: string[],
 *   username: string | null,
 * }}
 */
export function pillsToPersonTags(pills) {
  const list = Array.isArray(pills) ? pills : [];
  const names = list
    .filter((p) => normalizePersonPillType(p.type) === 'name')
    .map((p) => normalizeWhitespace(p.text))
    .filter(Boolean);
  const locationTags = list
    .filter((p) => normalizePersonPillType(p.type) === 'location')
    .map((p) => titleCasePhrase(p.text))
    .filter(Boolean);
  const contextTags = list
    .filter((p) => normalizePersonPillType(p.type) === 'focus')
    .map((p) => titleCasePhrase(p.text))
    .filter(Boolean);
  const usernameTags = list
    .filter((p) => normalizePersonPillType(p.type) === 'username')
    .map((p) => String(p.text || '').replace(/^@/, '').trim())
    .filter(Boolean);

  return {
    name: names.join(' ') || '',
    locationTags,
    contextTags,
    usernameTags,
    username: usernameTags[0] || null,
  };
}

/**
 * Build hero pills from resolved person tags (e.g. Corporate → Person pivot).
 *
 * @param {{
 *   name?: string,
 *   locationTags?: string[],
 *   contextTags?: string[],
 *   usernameTags?: string[],
 * }} tags
 * @returns {{ id: string, type: PersonPillType, text: string }[]}
 */
export function buildPersonPillsFromTags(tags = {}) {
  const pills = [];
  const name = normalizeWhitespace(tags?.name);
  if (name) {
    pills.push({ id: nextPillId(), type: 'name', text: titleCasePhrase(name) });
  }
  for (const loc of tags?.locationTags || []) {
    const text = titleCasePhrase(loc);
    if (!text) continue;
    pills.push({ id: nextPillId(), type: 'location', text });
  }
  for (const ctx of tags?.contextTags || []) {
    const text = titleCasePhrase(ctx);
    if (!text) continue;
    pills.push({ id: nextPillId(), type: 'focus', text });
  }
  for (const user of tags?.usernameTags || []) {
    const text = String(user || '')
      .replace(/^@/, '')
      .trim();
    if (!text) continue;
    pills.push({ id: nextPillId(), type: 'username', text });
  }
  return pills;
}

/**
 * Remove a pill by id.
 *
 * @param {{ id: string }[]} pills
 * @param {string} pillId
 * @returns {{ id: string }[]}
 */
export function removePillById(pills, pillId) {
  return (pills || []).filter((p) => p.id !== pillId);
}
